import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ChatBubbleOutline as CommentIcon,
} from '@mui/icons-material';
import { SubTask, User, aiBuilderAPI, userAPI } from '../services/api';
import CommentSection from './CommentSection';

interface SubTasksTableProps {
  actionItemId: number;
  subtasks: SubTask[];
  canGenerate: boolean;
  isGenerating: boolean;
  companyId: number;
  onSubTasksChange: () => void;
  assignableUsers?: User[];  // Optional - if provided, skips fetching
  highlightUserId?: number;  // Optional - highlights subtasks assigned to this user
}

interface EditableSubTask extends SubTask {
  isEditing?: boolean;
}

const SubTasksTable: React.FC<SubTasksTableProps> = ({
  actionItemId,
  subtasks,
  canGenerate,
  isGenerating,
  companyId,
  onSubTasksChange,
  assignableUsers: propAssignableUsers,
  highlightUserId,
}) => {
  const [items, setItems] = useState<EditableSubTask[]>(subtasks);
  const [assignableUsers, setAssignableUsers] = useState<User[]>(propAssignableUsers || []);
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [commentUpdateTrigger, setCommentUpdateTrigger] = useState(0);

  React.useEffect(() => {
    setItems(subtasks);
  }, [subtasks]);

  // Update assignable users if prop changes
  React.useEffect(() => {
    if (propAssignableUsers && propAssignableUsers.length > 0) {
      setAssignableUsers(propAssignableUsers);
    }
  }, [propAssignableUsers]);

  // Load assignable users for this company (only if not provided via props)
  React.useEffect(() => {
    // Skip if users were provided via props (even empty array means parent is managing this)
    // Only fetch if propAssignableUsers is undefined (not provided at all)
    if (propAssignableUsers !== undefined) {
      return;
    }

    const loadAssignableUsers = async () => {
      try {
        const users = await userAPI.getAssignableUsers(companyId);
        setAssignableUsers(users);
      } catch (error) {
        console.error('Error loading assignable users for subtasks:', error);
      }
    };

    if (companyId) {
      loadAssignableUsers();
    }
  }, [companyId, propAssignableUsers]);

  const handleEdit = (subTaskId: number) => {
    setItems(prev => prev.map(item => 
      item.id === subTaskId 
        ? { ...item, isEditing: true }
        : item
    ));
  };

  const handleSave = async (subTaskId: number) => {
    try {
      // Find the original item to compare changes
      const originalItem = items.find(item => item.id === subTaskId);
      if (!originalItem) {
        console.error('Original item not found for ID:', subTaskId);
        return;
      }

      // Get values from DOM inputs
      const titleInput = document.querySelector(`[data-subtask-field="${subTaskId}-title"]`) as HTMLInputElement;
      const startDateInput = document.querySelector(`[data-subtask-field="${subTaskId}-start_date"]`) as HTMLInputElement;
      const dueDateInput = document.querySelector(`[data-subtask-field="${subTaskId}-due_date"]`) as HTMLInputElement;
      const assignedToInput = document.querySelector(`[data-subtask-field="${subTaskId}-assigned_to"]`) as HTMLSelectElement;
      const statusInput = document.querySelector(`[data-subtask-field="${subTaskId}-status"]`) as HTMLSelectElement;

      const updates: Partial<SubTask> = {};
      
      // Only include fields that have actually changed
      if (titleInput?.value !== undefined && titleInput.value !== originalItem.title) {
        updates.title = titleInput.value;
      }
      if (startDateInput?.value && startDateInput.value !== originalItem.start_date) {
        updates.start_date = startDateInput.value;
      }
      if (dueDateInput?.value && dueDateInput.value !== originalItem.due_date) {
        updates.due_date = dueDateInput.value;
      }
      
      // Handle assigned_to changes (compare user IDs)
      const newAssignedTo = assignedToInput?.value ? parseInt(assignedToInput.value) : null;
      if (newAssignedTo !== originalItem.assigned_to) {
        updates.assigned_to = newAssignedTo;
      }
      
      if (statusInput?.value && statusInput.value !== originalItem.status) {
        updates.status = statusInput.value as SubTask['status'];
      }

      // Only make API call if there are actually changes
      if (Object.keys(updates).length === 0) {
        console.log('No changes detected, skipping API call');
        setItems(prev => prev.map(i => 
          i.id === subTaskId 
            ? { ...i, isEditing: false }
            : i
        ));
        return;
      }

      console.log('Updating subtask with changes:', updates);
      await aiBuilderAPI.updateSubTask(subTaskId, updates);
      
      setItems(prev => prev.map(i => 
        i.id === subTaskId 
          ? { ...i, isEditing: false }
          : i
      ));
      
      onSubTasksChange();
    } catch (error) {
      console.error('Error updating sub-task:', error);
    }
  };

  const handleCancel = (subTaskId: number) => {
    setItems(prev => prev.map(item => 
      item.id === subTaskId 
        ? { ...item, isEditing: false }
        : item
    ));
  };

  const handleDelete = async (subTaskId: number) => {
    if (window.confirm('Are you sure you want to delete this sub-task?')) {
      try {
        await aiBuilderAPI.deleteSubTask(subTaskId);
        onSubTasksChange();
      } catch (error) {
        console.error('Error deleting sub-task:', error);
      }
    }
  };

  const toggleComments = (subTaskId: number) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subTaskId)) {
        newSet.delete(subTaskId);
      } else {
        newSet.add(subTaskId);
      }
      return newSet;
    });
  };





  const handleAddNew = async () => {
    try {
      await aiBuilderAPI.createSubTask(actionItemId, {
        title: 'New sub-task',
        status: 'not_started',
        order: items.length + 1
      });
      onSubTasksChange();
    } catch (error) {
      console.error('Error creating sub-task:', error);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const EditableSubTaskCell: React.FC<{
    item: EditableSubTask;
    field: keyof SubTask;
    type: 'text' | 'select' | 'date';
    options?: { value: string; label: string }[];
    dataField: string;
    displayField?: keyof SubTask;
  }> = ({ item, field, type, options, dataField, displayField }) => {
    const value = item[field];
    
    // Determine what to display
    let finalDisplayValue;
    if (displayField) {
      // Use displayField for showing user names
      finalDisplayValue = item[displayField];
    } else if (field === 'start_date' || field === 'due_date') {
      // Format dates
      finalDisplayValue = formatDate(value as string);
    } else if (type === 'select' && options) {
      // For select fields, show the label instead of the raw value
      const option = options.find(opt => opt.value === value);
      finalDisplayValue = option ? option.label : value;
    } else {
      // Default: show the raw value
      finalDisplayValue = value;
    }

    if (!item.isEditing) {
      return (
        <Typography variant="body2" sx={{ minHeight: '20px', color: 'text.primary' }}>
          {finalDisplayValue || '-'}
        </Typography>
      );
    }

    switch (type) {
      case 'select':
        return (
          <FormControl size="small" fullWidth>
            <Select
              defaultValue={value || ''}
              inputProps={{ 'data-subtask-field': dataField }}
            >
              {options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'date':
        return (
          <TextField
            type="date"
            size="small"
            fullWidth
            defaultValue={value ? new Date(value as string).toISOString().split('T')[0] : ''}
            inputProps={{ 'data-subtask-field': dataField }}
            InputLabelProps={{ shrink: true }}
          />
        );
      default:
        return (
          <TextField
            size="small"
            fullWidth
            defaultValue={value || ''}
            inputProps={{ 'data-subtask-field': dataField }}
          />
        );
    }
  };

  if (items.length === 0) {
    if (isGenerating) {
      return (
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
            Loading sub-tasks...
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This may take a few seconds
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add Manual Sub-Task
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" color="primary">
          Sub-Tasks ({items.length})
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add
        </Button>
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Task</TableCell>
              <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Start</TableCell>
              <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Due</TableCell>
              <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Assigned</TableCell>
              <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const isHighlighted = highlightUserId !== undefined && item.assigned_to === highlightUserId;
              return (
              <React.Fragment key={item.id}>
              <TableRow
                sx={{
                  bgcolor: isHighlighted ? 'rgba(25, 118, 210, 0.12)' : undefined,
                  '&:hover': {
                    bgcolor: isHighlighted ? 'rgba(25, 118, 210, 0.2)' : undefined,
                  },
                  // Add a left border indicator for highlighted rows
                  borderLeft: isHighlighted ? '4px solid' : undefined,
                  borderLeftColor: isHighlighted ? 'primary.main' : undefined,
                }}
              >
                <TableCell>
                  <EditableSubTaskCell
                    item={item}
                    field="title"
                    type="text"
                    dataField={`${item.id}-title`}
                  />
                </TableCell>
                <TableCell>
                  <EditableSubTaskCell
                    item={item}
                    field="start_date"
                    type="date"
                    dataField={`${item.id}-start_date`}
                  />
                </TableCell>
                <TableCell>
                  <EditableSubTaskCell
                    item={item}
                    field="due_date"
                    type="date"
                    dataField={`${item.id}-due_date`}
                  />
                </TableCell>
                <TableCell>
                  <EditableSubTaskCell
                    item={item}
                    field="assigned_to"
                    type="select"
                    dataField={`${item.id}-assigned_to`}
                    options={[
                      { value: '', label: 'Unassigned' },
                      ...assignableUsers.map(user => ({
                        value: user.id.toString(),
                        label: `${user.first_name} ${user.last_name} (${user.username})`
                      }))
                    ]}
                    displayField="assigned_to_name"
                  />
                </TableCell>
                <TableCell>
                  <EditableSubTaskCell
                    item={item}
                    field="status"
                    type="select"
                    dataField={`${item.id}-status`}
                    options={[
                      { value: 'not_started', label: 'Not Started' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' },
                    ]}
                  />
                </TableCell>
                <TableCell>
                  {item.isEditing ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleSave(item.id)}
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleCancel(item.id)}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleEdit(item.id)}
                      >
                        <EditIcon />
                      </IconButton>
                       <CommentSection
                         key={`subtask-toggle-${item.id}-${commentUpdateTrigger}`}
                         entityType="subtask"
                         entityId={item.id}
                         onCommentsChange={() => {
                           setCommentUpdateTrigger(prev => prev + 1);
                           onSubTasksChange();
                         }}
                         showToggleButton={true}
                         isExpanded={expandedComments.has(item.id)}
                         onToggleExpand={() => toggleComments(item.id)}
                         initialCommentCount={item.comment_count}
                       />
                      <IconButton 
                        size="small"
                        color="error"
                        onClick={() => handleDelete(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
              {expandedComments.has(item.id) && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, bgcolor: 'background.paper' }}>
                                         <CommentSection
                       key={`subtask-expanded-${item.id}-${commentUpdateTrigger}`}
                       entityType="subtask"
                       entityId={item.id}
                       onCommentsChange={() => {
                         setCommentUpdateTrigger(prev => prev + 1);
                         onSubTasksChange();
                       }}
                       alwaysExpanded={true}
                       initialCommentCount={item.comment_count}
                     />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SubTasksTable; 